import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users,
  User,
  Search,
  MessageCircle,
  Send,
  Heart,
  UserCircle,
  MapPin,
  X,
  Check,
  MoreHorizontal,
  Trash2,
  Ban,
  AlertTriangle,
  Eye,
  FileText,
  ShieldAlert,
  PlusCircle,
  Phone,
  Copy,
  Share2,
  ShoppingBag,
  Camera,
  ChevronRight
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Textarea } from "./ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import UserProfileModal from "./UserProfileModal";
import { format } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const FindGolfersPage = ({ subscription, user, fetchUnreadCount, playMessageChime }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zipCode, setZipCode] = useState('');
  const [handicapRange, setHandicapRange] = useState('any');
  const [agePreference, setAgePreference] = useState(false);
  const [searching, setSearching] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePreview, setProfilePreview] = useState(user?.picture || null);
  const [activeTab, setActiveTab] = useState('feed'); // 'feed', 'search' or 'messages'
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [message, setMessage] = useState('');
  const [conversations, setConversations] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  // New Chat Modal State
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [isGroupChat, setIsGroupChat] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [creatingChat, setCreatingChat] = useState(false);
  // Profile View State
  const [viewingProfileId, setViewingProfileId] = useState(null);
  // Member Directory State
  const [allMembers, setAllMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [hasMoreMembers, setHasMoreMembers] = useState(false);
  const [memberSkip, setMemberSkip] = useState(0);
  // Social Feed State
  const [posts, setPosts] = useState([]);
  const [newPostContent, setNewPostContent] = useState('');
  const [submittingPost, setSubmittingPost] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [showPostEntry, setShowPostEntry] = useState(false);
  const [showCommunityStandards, setShowCommunityStandards] = useState(false);
  // Buy/Sell State
  const [showBuySellModal, setShowBuySellModal] = useState(false);
  const [buySellData, setBuySellData] = useState({
    description: '',
    condition: 'good',
    price: '',
    location: '',
    contactPreference: 'message'
  });
  const [submittingBuySell, setSubmittingBuySell] = useState(false);
  const [hasAgreedToStandards, setHasAgreedToStandards] = useState(() => {
    return localStorage.getItem('hasAgreedToGolfConnectStandards') === 'true';
  });
  // Post Image State
  const [postImage, setPostImage] = useState(null);
  const [postImagePreview, setPostImagePreview] = useState(null);
  const [compressingImage, setCompressingImage] = useState(false);
  // Invite State
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitePhone, setInvitePhone] = useState('');
  const [inviteStep, setInviteStep] = useState('input'); // 'input', 'preview'
  const [inviteLink, setInviteLink] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Fetch conversations on initial load to show unread badge
  useEffect(() => {
    fetchConversations();
  }, []);

  // Fetch conversations when messages tab is active
  useEffect(() => {
    if (activeTab === 'messages') {
      fetchConversations();
    }
  }, [activeTab]);

  // Fetch posts when feed tab is active
  useEffect(() => {
    if (activeTab === 'feed') {
      fetchPosts();
    }
  }, [activeTab, loading]);

  // Fetch members when search tab is active
  useEffect(() => {
    if (activeTab === 'search') {
      fetchAllMembers();
    }
  }, [activeTab]);

  const fetchAllMembers = async (skip = 0, append = false) => {
    setLoadingMembers(true);
    try {
      const response = await axios.get(`${API}/users/all?skip=${skip}&limit=50`);
      if (append) {
        setAllMembers(prev => [...prev, ...response.data.users]);
      } else {
        setAllMembers(response.data.users);
      }
      setHasMoreMembers(response.data.has_more);
      setMemberSkip(skip + 50);
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadMoreMembers = () => {
    fetchAllMembers(memberSkip, true);
  };

  const filteredMembers = memberSearch.length >= 2
    ? allMembers.filter(m => 
        m.name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
        m.username?.toLowerCase().includes(memberSearch.toLowerCase()) ||
        m.location?.toLowerCase().includes(memberSearch.toLowerCase())
      )
    : allMembers;

  const startConversationWithMember = async (member) => {
    try {
      const response = await axios.post(`${API}/conversations`, {
        participant_ids: [member.user_id],
        is_group: false
      });
      setActiveTab('messages');
      await fetchConversations();
      handleSelectConversation(response.data);
      toast.success(`Started conversation with ${member.name}`);
    } catch (error) {
      toast.error("Failed to start conversation");
    }
  };

  const fetchConversations = async () => {
    setLoadingConversations(true);
    try {
      const response = await axios.get(`${API}/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoadingConversations(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`);
      setChatMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSelectConversation = async (conv) => {
    setSelectedConversation(conv);
    await fetchMessages(conv.conversation_id);
    // Mark as read
    await axios.post(`${API}/conversations/${conv.conversation_id}/read`).catch(() => {});
    // Update local unread count
    setConversations(prev => prev.map(c => 
      c.conversation_id === conv.conversation_id ? { ...c, unread_count: 0 } : c
    ));
    // Refresh global unread count
    fetchUnreadCount();
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !selectedConversation) return;
    setSendingMessage(true);
    try {
      const response = await axios.post(`${API}/conversations/${selectedConversation.conversation_id}/messages`, {
        content: message
      });
      setChatMessages(prev => [...prev, response.data]);
      setMessage('');
      // Update conversation's last message
      setConversations(prev => prev.map(c => 
        c.conversation_id === selectedConversation.conversation_id 
          ? { ...c, last_message: message, last_message_at: new Date().toISOString() }
          : c
      ));
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSearchUsers = async (query) => {
    setUserSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearchingUsers(true);
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data.users || response.data || []);
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleSelectUser = (selectedUser) => {
    if (selectedUsers.find(u => u.user_id === selectedUser.user_id)) {
      setSelectedUsers(prev => prev.filter(u => u.user_id !== selectedUser.user_id));
    } else {
      setSelectedUsers(prev => [...prev, selectedUser]);
    }
    setUserSearch('');
    setSearchResults([]);
  };

  const handleCreateConversation = async () => {
    if (selectedUsers.length === 0) return;
    if (isGroupChat && !groupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }
    setCreatingChat(true);
    try {
      const response = await axios.post(`${API}/conversations`, {
        participant_ids: selectedUsers.map(u => u.user_id),
        name: isGroupChat ? groupName : null,
        is_group: isGroupChat || selectedUsers.length > 1
      });
      setShowNewChatModal(false);
      setSelectedUsers([]);
      setGroupName('');
      setIsGroupChat(false);
      await fetchConversations();
      handleSelectConversation(response.data);
      toast.success(isGroupChat ? "Group chat created!" : "Conversation started!");
    } catch (error) {
      toast.error("Failed to create conversation");
    } finally {
      setCreatingChat(false);
    }
  };

  const getConversationName = (conv) => {
    if (conv.is_group) {
      return conv.name || `Group (${conv.participants?.length || 0} members)`;
    }
    return conv.other_user?.name || 'Unknown User';
  };

  const getConversationAvatar = (conv) => {
    if (conv.is_group) {
      return conv.name?.charAt(0)?.toUpperCase() || 'G';
    }
    return conv.other_user?.name?.charAt(0)?.toUpperCase() || 'U';
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const fetchPosts = async () => {
    setLoadingPosts(true);
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoadingPosts(false);
    }
  };

  // Compress image to reduce size (max 300KB)
  const compressImage = (file, maxSizeKB = 300) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Max dimensions
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          
          if (width > MAX_WIDTH) {
            height = (height * MAX_WIDTH) / width;
            width = MAX_WIDTH;
          }
          if (height > MAX_HEIGHT) {
            width = (width * MAX_HEIGHT) / height;
            height = MAX_HEIGHT;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Start with quality 0.8 and reduce if needed
          let quality = 0.8;
          let dataUrl = canvas.toDataURL('image/jpeg', quality);
          
          // Reduce quality until under maxSizeKB
          while (dataUrl.length > maxSizeKB * 1024 * 1.37 && quality > 0.1) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
          }
          
          resolve(dataUrl);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Handle post image selection
  const handlePostImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large (max 10MB)");
      return;
    }
    
    setCompressingImage(true);
    try {
      const compressed = await compressImage(file);
      setPostImage(compressed);
      setPostImagePreview(compressed);
      toast.success("Image ready to post!");
    } catch (error) {
      toast.error("Failed to process image");
    } finally {
      setCompressingImage(false);
    }
  };

  // Clear post image
  const clearPostImage = () => {
    setPostImage(null);
    setPostImagePreview(null);
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() && !postImage) {
      toast.error("Add some text or an image");
      return;
    }
    
    // Show community standards popup for first-time posters
    if (!hasAgreedToStandards) {
      setShowCommunityStandards(true);
      return;
    }
    
    setSubmittingPost(true);
    try {
      const response = await axios.post(`${API}/posts`, {
        content: newPostContent,
        post_type: 'general',
        image_url: postImage,
        visibility: 'public'
      });
      setPosts([response.data, ...posts]);
      setNewPostContent('');
      clearPostImage();
      setShowPostEntry(false);
      toast.success("Post shared!");
    } catch (error) {
      console.error("Failed to create post:", error);
      toast.error("Failed to create post");
    } finally {
      setSubmittingPost(false);
    }
  };

  const handleAgreeToStandards = async () => {
    localStorage.setItem('hasAgreedToGolfConnectStandards', 'true');
    setHasAgreedToStandards(true);
    setShowCommunityStandards(false);
    
    // Now actually submit the post
    setSubmittingPost(true);
    try {
      const response = await axios.post(`${API}/posts`, {
        content: newPostContent,
        post_type: 'general',
        image_url: postImage,
        visibility: 'public'
      });
      setPosts([response.data, ...posts]);
      setNewPostContent('');
      clearPostImage();
      setShowPostEntry(false);
      toast.success("Post shared!");
    } catch (error) {
      console.error("Failed to create post:", error);
      toast.error("Failed to create post");
    } finally {
      setSubmittingPost(false);
    }
  };

  // Handle Buy/Sell listing creation
  const handleCreateBuySell = async () => {
    if (!buySellData.description.trim()) {
      toast.error("Please add a description");
      return;
    }
    if (!buySellData.price.trim()) {
      toast.error("Please add an asking price");
      return;
    }
    
    setSubmittingBuySell(true);
    try {
      const response = await axios.post(`${API}/posts`, {
        content: buySellData.description,
        post_type: 'buy_sell',
        buy_sell_data: {
          condition: buySellData.condition,
          price: buySellData.price,
          location: buySellData.location,
          contact_preference: buySellData.contactPreference
        }
      });
      setPosts([response.data, ...posts]);
      // Reset form
      setBuySellData({
        description: '',
        condition: 'good',
        price: '',
        location: '',
        contactPreference: 'message'
      });
      setShowBuySellModal(false);
      toast.success("Listing posted!");
    } catch (error) {
      toast.error("Failed to create listing");
    } finally {
      setSubmittingBuySell(false);
    }
  };

  const handleLikePost = async (postId) => {
    try {
      const response = await axios.post(`${API}/posts/${postId}/like`);
      setPosts(posts.map(p => 
        p.post_id === postId 
          ? { ...p, is_liked: response.data.liked, likes_count: response.data.likes_count }
          : p
      ));
    } catch (error) {
      toast.error("Failed to like post");
    }
  };

  const handleAddComment = async (postId) => {
    const content = commentInputs[postId];
    if (!content?.trim()) return;
    try {
      const response = await axios.post(`${API}/posts/${postId}/comment`, { content });
      setPosts(posts.map(p => 
        p.post_id === postId 
          ? { ...p, comments: [...(p.comments || []), response.data], comments_count: (p.comments_count || 0) + 1 }
          : p
      ));
      setCommentInputs({ ...commentInputs, [postId]: '' });
    } catch (error) {
      toast.error("Failed to add comment");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await axios.delete(`${API}/posts/${postId}`);
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success("Post deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    }
  };

  const [reportingContent, setReportingContent] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  const handleReport = async () => {
    if (!reportReason || !reportingContent) return;
    setSubmittingReport(true);
    try {
      await axios.post(`${API}/reports`, {
        content_type: reportingContent.type,
        content_id: reportingContent.id,
        post_id: reportingContent.postId,
        reason: reportReason,
        details: reportDetails
      });
      toast.success("Report submitted. Thank you for helping keep our community safe.");
      setReportingContent(null);
      setReportReason('');
      setReportDetails('');
    } catch (error) {
      if (error.response?.data?.detail?.includes("already reported")) {
        toast.info("You have already reported this content.");
      } else {
        toast.error("Failed to submit report");
      }
    } finally {
      setSubmittingReport(false);
    }
  };

  const toggleComments = (postId) => {
    setExpandedComments({ ...expandedComments, [postId]: !expandedComments[postId] });
  };

  const getPostTypeLabel = (type) => {
    const labels = {
      general: 'General',
      round_update: 'Round Update',
      looking_for_group: 'Looking for Group',
      course_review: 'Course Review',
      buy_sell: 'For Sale'
    };
    return labels[type] || 'General';
  };

  const getPostTypeColor = (type) => {
    const colors = {
      general: 'bg-stone-100 text-stone-600',
      round_update: 'bg-green-100 text-green-700',
      looking_for_group: 'bg-blue-100 text-blue-700',
      course_review: 'bg-amber-100 text-amber-700',
      buy_sell: 'bg-purple-100 text-purple-700'
    };
    return colors[type] || colors.general;
  };

  const getConditionLabel = (condition) => {
    const labels = {
      new: 'New',
      like_new: 'Like New',
      good: 'Good',
      fair: 'Fair',
      poor: 'Poor'
    };
    return labels[condition] || condition;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setInvitePhone(formatted);
  };

  // Create invite and show preview
  const handleCreateInvite = async () => {
    const phoneNumbers = invitePhone.replace(/\D/g, '');
    if (phoneNumbers.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    
    setInviteLoading(true);
    try {
      const response = await axios.post(`${API}/invites/create`, {
        phone: phoneNumbers
      });
      setInviteLink(response.data.invite_url);
      setInviteStep('preview');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invite");
    } finally {
      setInviteLoading(false);
    }
  };

  // Send SMS via native messaging
  const handleSendSMS = () => {
    const message = `🦅 ${user?.name || 'A friend'} invited you to Par Seeker!\n\nOrganize golf games, track scores, fair handicaps, and settle up easy. No more arguments!\n\nJoin here: ${inviteLink}`;
    const phoneNumbers = invitePhone.replace(/\D/g, '');
    
    // Use SMS URL scheme
    const smsUrl = `sms:+1${phoneNumbers}?&body=${encodeURIComponent(message)}`;
    window.location.href = smsUrl;
    
    toast.success("Opening Messages...");
    
    // Reset modal after short delay
    setTimeout(() => {
      setShowInviteModal(false);
      setInviteStep('input');
      setInvitePhone('');
      setInviteLink('');
    }, 1000);
  };

  // Copy invite link
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Link copied!");
    } catch {
      toast.error("Failed to copy link");
    }
  };

  // Reset invite modal
  const resetInviteModal = () => {
    setShowInviteModal(false);
    setInviteStep('input');
    setInvitePhone('');
    setInviteLink('');
  };

  const isSubscribed = subscription?.is_subscribed;
  const hasFreeTrial = !subscription?.trial_expired;
  const hasHandicap = stats?.handicap !== null && stats?.handicap !== undefined;
  // Feature unlocked for all users - no restrictions
  const isFeatureUnlocked = true;

  const handlePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      setProfilePicture(file);
      setProfilePreview(URL.createObjectURL(file));
      toast.success("Profile picture updated!");
    }
  };

  const handleSearch = () => {
    if (!isFeatureUnlocked) return;
    setSearching(true);
    setTimeout(() => {
      toast.info("Golfer network launching soon! You'll be notified when players are nearby.");
      setSearching(false);
    }, 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className={`mx-auto animate-fade-in ${activeTab === 'messages' ? 'max-w-6xl' : 'max-w-4xl'}`} data-testid="golf-connect-page">
      {/* Profile View Modal */}
      {viewingProfileId && (
        <UserProfileModal 
          userId={viewingProfileId} 
          onClose={() => setViewingProfileId(null)} 
        />
      )}
      
      {/* Tab Navigation */}
      <div className="flex justify-between mb-6 border-b border-stone-200 pb-2">
        <button
          onClick={() => setActiveTab('feed')}
          className={`font-bold text-sm transition-colors flex items-center gap-2 pb-2 ${
            activeTab === 'feed' 
              ? 'text-green-700 border-b-2 border-green-700 -mb-[10px]' 
              : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          <FileText className="w-4 h-4" />
          Feed
        </button>
        <button
          onClick={() => setActiveTab('search')}
          className={`font-bold text-sm transition-colors flex items-center gap-2 pb-2 ${
            activeTab === 'search' 
              ? 'text-green-700 border-b-2 border-green-700 -mb-[10px]' 
              : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          <Users className="w-4 h-4" />
          Members
        </button>
        <button
          onClick={() => setActiveTab('messages')}
          className={`font-bold text-sm transition-colors flex items-center gap-2 pb-2 relative ${
            activeTab === 'messages' 
              ? 'text-green-700 border-b-2 border-green-700 -mb-[10px]' 
              : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          <MessageCircle className="w-4 h-4" />
          Messenger
          {conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0) > 0 && (
            <span className="min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
              {conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)}
            </span>
          )}
        </button>
      </div>

      {/* Unread Messages Banner */}
      {activeTab !== 'messages' && conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0) > 0 && (
        <button 
          onClick={() => setActiveTab('messages')}
          className="w-full mb-6 p-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl text-white flex items-center justify-between hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg animate-fade-in"
          data-testid="unread-messages-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div className="text-left">
              <p className="font-semibold">
                You have {conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)} unread message{conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0) !== 1 ? 's' : ''}
              </p>
              <p className="text-sm text-blue-100">Tap to view your messages</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {/* Report Modal */}
      {reportingContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ShieldAlert className="w-5 h-5 text-red-500" />
                  Report Content
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setReportingContent(null)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <CardDescription>
                Help us keep the Golf Connect community safe
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Why are you reporting this?</Label>
                <Select value={reportReason} onValueChange={setReportReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="spam">Spam or misleading</SelectItem>
                    <SelectItem value="harassment">Harassment or bullying</SelectItem>
                    <SelectItem value="hate_speech">Hate speech or discrimination</SelectItem>
                    <SelectItem value="inappropriate">Inappropriate content</SelectItem>
                    <SelectItem value="false_info">False information</SelectItem>
                    <SelectItem value="other">Something else</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Additional details (optional)</Label>
                <Textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  placeholder="Provide any additional context..."
                  rows={3}
                />
              </div>
              <div className="bg-stone-50 rounded-lg p-3 text-xs text-stone-600">
                <p className="font-medium mb-1">What happens next?</p>
                <p>Our team will review this report within 24 hours. If we find a violation of our Community Standards, we'll take appropriate action.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setReportingContent(null)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleReport} 
                  disabled={!reportReason || submittingReport}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {submittingReport ? (
                    <div className="spinner w-4 h-4 border-white border-t-transparent" />
                  ) : (
                    'Submit Report'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Feed Tab */}
      {activeTab === 'feed' && (
        <div className={!isFeatureUnlocked ? 'opacity-50 pointer-events-none' : ''}>
          {/* Three Icons Navigation - Post (Eagle), Invite (Golf Ball), Buy/Sell (Golf Ball) */}
          {!showPostEntry && (
            <div className="mb-6 flex justify-center items-end gap-6">
              {/* Invite Golf Ball */}
              <button
                onClick={() => setShowInviteModal(true)}
                className="flex flex-col items-center transition-all hover:scale-110 hover:drop-shadow-lg"
                data-testid="open-invite-btn"
              >
                <img 
                  src="/images/golf-ball.png" 
                  alt="Invite" 
                  className="w-16 h-16 object-contain"
                />
                <span className="text-sm font-bold text-stone-700 mt-1">Invite</span>
              </button>
              
              {/* Post Eagle (Center - Larger) */}
              <button
                onClick={() => setShowPostEntry(true)}
                className="flex flex-col items-center transition-all hover:scale-110 hover:drop-shadow-lg"
                data-testid="open-post-entry-btn"
              >
                <img 
                  src="/images/chat-logo.png" 
                  alt="Post" 
                  className="w-20 h-20 object-contain"
                />
                <span className="text-sm font-bold text-stone-700 mt-1">Post</span>
              </button>
              
              {/* Buy/Sell Golf Ball */}
              <button
                onClick={() => setShowBuySellModal(true)}
                className="flex flex-col items-center transition-all hover:scale-110 hover:drop-shadow-lg"
                data-testid="open-buysell-btn"
              >
                <img 
                  src="/images/golf-ball.png" 
                  alt="Buy/Sell" 
                  className="w-16 h-16 object-contain"
                />
                <span className="text-sm font-bold text-stone-700 mt-1">Buy/Sell</span>
              </button>
            </div>
          )}

          {/* Buy/Sell Modal */}
          <Dialog open={showBuySellModal} onOpenChange={setShowBuySellModal}>
            <DialogContent className="sm:max-w-md bg-white">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl">
                  <ShoppingBag className="w-6 h-6 text-green-700" />
                  Create Listing
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Description */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-stone-700">Description</label>
                  <Textarea
                    value={buySellData.description}
                    onChange={(e) => setBuySellData({...buySellData, description: e.target.value})}
                    placeholder="What are you selling? (e.g., TaylorMade Stealth Driver)"
                    className="min-h-[80px] resize-none"
                    data-testid="buysell-description"
                  />
                </div>
                
                {/* Condition */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-stone-700">Condition</label>
                  <select
                    value={buySellData.condition}
                    onChange={(e) => setBuySellData({...buySellData, condition: e.target.value})}
                    className="w-full h-10 px-3 rounded-md border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    data-testid="buysell-condition"
                  >
                    <option value="new">New</option>
                    <option value="like_new">Like New</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </select>
                </div>
                
                {/* Price */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-stone-700">Asking Price</label>
                  <div className="flex items-center">
                    <span className="text-stone-500 mr-2">$</span>
                    <Input
                      type="text"
                      value={buySellData.price}
                      onChange={(e) => setBuySellData({...buySellData, price: e.target.value.replace(/[^0-9.]/g, '')})}
                      placeholder="0.00"
                      className="flex-1"
                      data-testid="buysell-price"
                    />
                  </div>
                </div>
                
                {/* Location */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-stone-700">Location</label>
                  <Input
                    type="text"
                    value={buySellData.location}
                    onChange={(e) => setBuySellData({...buySellData, location: e.target.value})}
                    placeholder="City, State (e.g., Austin, TX)"
                    data-testid="buysell-location"
                  />
                </div>
                
                {/* Contact Preference */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-stone-700">Contact Preference</label>
                  <select
                    value={buySellData.contactPreference}
                    onChange={(e) => setBuySellData({...buySellData, contactPreference: e.target.value})}
                    className="w-full h-10 px-3 rounded-md border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    data-testid="buysell-contact"
                  >
                    <option value="message">Message on Par Seeker</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone/Text</option>
                  </select>
                </div>
                
                {/* Submit Button */}
                <Button
                  onClick={handleCreateBuySell}
                  disabled={submittingBuySell || !buySellData.description.trim() || !buySellData.price.trim()}
                  className="w-full h-12 bg-green-700 hover:bg-green-800 text-lg font-bold"
                  data-testid="submit-buysell-btn"
                >
                  {submittingBuySell ? (
                    <div className="spinner w-5 h-5 border-white border-t-transparent" />
                  ) : (
                    <>
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      Post Listing
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Invite Modal */}
          <Dialog open={showInviteModal} onOpenChange={(open) => !open && resetInviteModal()}>
            <DialogContent className="sm:max-w-md bg-white">
              <DialogHeader>
                <DialogTitle className="flex items-center justify-center gap-2 text-xl">
                  <img src="/images/par-seeker-logo-full.png" alt="Par Seeker" className="w-16 h-16 object-contain" />
                </DialogTitle>
              </DialogHeader>
              
              {inviteStep === 'input' ? (
                <div className="space-y-6 py-4">
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-stone-800">Invite a Friend</h3>
                    <p className="text-sm text-stone-500 mt-1">Send an invite via text message</p>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Phone Number Input */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-stone-700">Phone Number</label>
                      <div className="flex items-center gap-2">
                        <span className="text-stone-500 font-medium">+1</span>
                        <Input
                          type="tel"
                          value={invitePhone}
                          onChange={handlePhoneChange}
                          placeholder="(555) 123-4567"
                          className="flex-1 h-12 text-lg"
                          maxLength={14}
                          data-testid="invite-phone-input"
                        />
                      </div>
                    </div>
                    
                    <Button
                      onClick={handleCreateInvite}
                      disabled={invitePhone.replace(/\D/g, '').length < 10 || inviteLoading}
                      className="w-full h-12 bg-green-700 hover:bg-green-800 text-lg font-bold"
                      data-testid="create-invite-btn"
                    >
                      {inviteLoading ? (
                        <div className="spinner w-5 h-5 border-white border-t-transparent" />
                      ) : (
                        <>
                          <Phone className="w-5 h-5 mr-2" />
                          Continue
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6 py-4">
                  {/* Invite Preview */}
                  <div className="bg-stone-50 rounded-xl p-6 text-center border-2 border-stone-200">
                    <img 
                      src="/images/par-seeker-logo-full.png" 
                      alt="Par Seeker" 
                      className="w-24 h-24 object-contain mx-auto mb-4"
                    />
                    <h3 className="text-xl font-black text-green-800 mb-2">
                      You've been invited by {user?.name || 'a friend'}!
                    </h3>
                    <p className="text-stone-600 text-sm">
                      Track your handicap. Play skins. Settle bets.
                    </p>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <Button
                      onClick={handleSendSMS}
                      className="w-full h-14 bg-green-700 hover:bg-green-800 text-lg font-bold"
                      data-testid="send-sms-btn"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Send Invite
                    </Button>
                    
                    <Button
                      onClick={handleCopyLink}
                      variant="outline"
                      className="w-full h-12"
                      data-testid="copy-link-btn"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link Instead
                    </Button>
                    
                    <button
                      onClick={() => setInviteStep('input')}
                      className="w-full text-sm text-stone-500 hover:text-stone-700"
                    >
                      ← Change number
                    </button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Create Post - Hidden by default */}
          {showPostEntry && (
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <User className="w-5 h-5 text-green-700" />
                    )}
                  </div>
                  <div className="flex-1">
                    <Textarea
                      value={newPostContent}
                      onChange={(e) => setNewPostContent(e.target.value)}
                      placeholder="Share something with the golf community..."
                      className="min-h-[80px] resize-none mb-3"
                      data-testid="post-content-input"
                      autoFocus
                    />
                    
                    {/* Image Preview */}
                    {postImagePreview && (
                      <div className="relative mb-3 inline-block">
                        <img 
                          src={postImagePreview} 
                          alt="Post preview" 
                          className="max-h-48 rounded-lg object-cover"
                        />
                        <button
                          onClick={clearPostImage}
                          className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between gap-2">
                      {/* Image Upload Button */}
                      <div className="flex items-center gap-2">
                        <label className="cursor-pointer flex items-center gap-2 text-sm text-stone-500 hover:text-green-600 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handlePostImageSelect}
                            className="hidden"
                            disabled={compressingImage}
                          />
                          {compressingImage ? (
                            <div className="spinner w-5 h-5 border-green-600 border-t-transparent" />
                          ) : (
                            <Camera className="w-5 h-5" />
                          )}
                          <span className="hidden sm:inline">Add Photo</span>
                        </label>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setShowPostEntry(false);
                            setNewPostContent('');
                            clearPostImage();
                          }}
                          className="text-sm text-stone-500 hover:text-stone-700 font-medium px-3 py-1"
                          data-testid="cancel-post-btn"
                        >
                          Cancel
                        </button>
                        <Button
                          onClick={handleCreatePost}
                          disabled={(!newPostContent.trim() && !postImage) || submittingPost || compressingImage}
                          className="btn-primary"
                          data-testid="submit-post-btn"
                        >
                          {submittingPost ? (
                            <div className="spinner w-4 h-4 border-white border-t-transparent" />
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Post
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Community Standards Modal */}
          <Dialog open={showCommunityStandards} onOpenChange={setShowCommunityStandards}>
            <DialogContent className="sm:max-w-md bg-white">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-green-600" />
                  Community Standards
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-sm text-stone-600">
                  Welcome to Golf Connect! Before you post, please agree to our community standards:
                </p>
                <ul className="text-sm text-stone-600 space-y-2 pl-4">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Be respectful and courteous to all golfers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>No harassment, hate speech, or inappropriate content</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Keep discussions golf-related and constructive</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Report any content that violates these standards</span>
                  </li>
                </ul>
                <p className="text-xs text-stone-500">
                  By posting, you agree to follow our{' '}
                  <button className="underline text-green-700 hover:text-green-800">Community Guidelines</button>.
                </p>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowCommunityStandards(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAgreeToStandards} className="btn-primary">
                  I Agree & Post
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Posts Feed */}
          {loadingPosts ? (
            <div className="flex items-center justify-center h-32">
              <div className="spinner" />
            </div>
          ) : posts.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="empty-state">
                <FileText className="empty-state-icon" />
                <h3 className="text-xl font-semibold text-stone-900 mb-2">No posts yet</h3>
                <p className="text-stone-500">Be the first to share something with the community!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {posts.map(post => (
                <Card key={post.post_id} data-testid={`post-${post.post_id}`}>
                  <CardContent className="p-4">
                    {/* Post Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <button 
                          onClick={() => post.user_id !== user?.user_id && setViewingProfileId(post.user_id)}
                          className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors"
                          title={post.user_id !== user?.user_id ? `View ${post.user_name}'s profile` : ''}
                        >
                          {post.user_picture ? (
                            <img src={post.user_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <span className="text-white font-semibold">
                              {post.user_name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          )}
                        </button>
                        <div>
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => post.user_id !== user?.user_id && setViewingProfileId(post.user_id)}
                              className={`font-semibold text-stone-900 ${post.user_id !== user?.user_id ? 'hover:text-blue-600 cursor-pointer' : ''}`}
                            >
                              {post.user_name}
                            </button>
                            {post.user_handicap !== null && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                {post.user_handicap} HCP
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-stone-400">{formatTimeAgo(post.created_at)}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getPostTypeColor(post.post_type)}`}>
                              {getPostTypeLabel(post.post_type)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {post.user_id === user?.user_id ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePost(post.post_id)}
                            className="text-stone-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setReportingContent({ type: 'post', id: post.post_id })}
                            className="text-stone-400 hover:text-amber-500"
                            title="Report post"
                          >
                            <AlertTriangle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Post Content */}
                    <p className="text-stone-700 whitespace-pre-wrap mb-4">{post.content}</p>

                    {/* Post Image */}
                    {post.image_url && (
                      <div className="mb-4 rounded-lg overflow-hidden">
                        <img 
                          src={post.image_url} 
                          alt="Post" 
                          className="w-full max-h-96 object-cover rounded-lg"
                          loading="lazy"
                        />
                      </div>
                    )}

                    {/* Buy/Sell Details */}
                    {post.post_type === 'buy_sell' && post.buy_sell && (
                      <div className="bg-purple-50 rounded-lg p-4 mb-4 border border-purple-100">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-purple-600 font-medium">Price:</span>
                            <span className="ml-2 text-stone-800 font-bold">${post.buy_sell.price}</span>
                          </div>
                          <div>
                            <span className="text-purple-600 font-medium">Condition:</span>
                            <span className="ml-2 text-stone-700">{getConditionLabel(post.buy_sell.condition)}</span>
                          </div>
                          {post.buy_sell.location && (
                            <div className="col-span-2">
                              <span className="text-purple-600 font-medium">Location:</span>
                              <span className="ml-2 text-stone-700">{post.buy_sell.location}</span>
                            </div>
                          )}
                          <div className="col-span-2">
                            <span className="text-purple-600 font-medium">Contact:</span>
                            <span className="ml-2 text-stone-700">
                              {post.buy_sell.contact_preference === 'message' ? 'Message on Par Seeker' : 
                               post.buy_sell.contact_preference === 'email' ? 'Email' : 'Phone/Text'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Post Actions */}
                    <div className="flex items-center gap-4 pt-3 border-t">
                      <button
                        onClick={() => handleLikePost(post.post_id)}
                        className={`flex items-center gap-2 text-sm transition-colors ${
                          post.is_liked ? 'text-red-500' : 'text-stone-500 hover:text-red-500'
                        }`}
                      >
                        <Heart className={`w-5 h-5 ${post.is_liked ? 'fill-current' : ''}`} />
                        <span>{post.likes_count || 0}</span>
                      </button>
                      <button
                        onClick={() => toggleComments(post.post_id)}
                        className="flex items-center gap-2 text-sm text-stone-500 hover:text-green-700 transition-colors"
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span>{post.comments_count || 0}</span>
                      </button>
                    </div>

                    {/* Comments Section */}
                    {expandedComments[post.post_id] && (
                      <div className="mt-4 pt-4 border-t">
                        {/* Existing Comments */}
                        {post.comments?.length > 0 && (
                          <div className="space-y-3 mb-4">
                            {post.comments.map(comment => (
                              <div key={comment.comment_id} className="flex gap-2">
                                <div className="w-8 h-8 bg-stone-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="text-stone-600 text-xs font-semibold">
                                    {comment.user_name?.charAt(0)?.toUpperCase() || 'U'}
                                  </span>
                                </div>
                                <div className="flex-1 bg-stone-50 rounded-lg p-2">
                                  <span className="font-medium text-sm text-stone-900">{comment.user_name}</span>
                                  <p className="text-sm text-stone-700">{comment.content}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Add Comment */}
                        <div className="flex gap-2">
                          <Input
                            value={commentInputs[post.post_id] || ''}
                            onChange={(e) => setCommentInputs({ ...commentInputs, [post.post_id]: e.target.value })}
                            placeholder="Write a comment..."
                            className="flex-1"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.post_id)}
                          />
                          <Button
                            onClick={() => handleAddComment(post.post_id)}
                            disabled={!commentInputs[post.post_id]?.trim()}
                            size="sm"
                            className="btn-primary"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search Tab */}
      {activeTab === 'search' && (
        <div>
          {/* Member Search Bar */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Search by name, username, or location..."
                  className="pl-10 h-12"
                  data-testid="member-search-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Member Directory */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-green-600" />
                Member Directory
              </CardTitle>
              <CardDescription>
                {filteredMembers.length} golfer{filteredMembers.length !== 1 ? 's' : ''} in the community
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingMembers && allMembers.length === 0 ? (
                <div className="flex justify-center py-8">
                  <div className="spinner w-8 h-8 border-green-600 border-t-transparent" />
                </div>
              ) : filteredMembers.length === 0 ? (
                <div className="text-center py-8 text-stone-500">
                  {memberSearch ? 'No golfers found matching your search' : 'No members yet'}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredMembers.map((member) => (
                    <div
                      key={member.user_id}
                      className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors"
                      data-testid={`member-card-${member.user_id}`}
                    >
                      {/* Avatar - Blue background with white initial */}
                      <div 
                        className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-all overflow-hidden flex-shrink-0"
                        onClick={() => setViewingProfileId(member.user_id)}
                      >
                        {member.picture ? (
                          <img src={member.picture} alt={member.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xl font-black text-white">
                            {member.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p 
                            className="font-bold text-stone-900 cursor-pointer hover:text-blue-700 transition-colors"
                            onClick={() => setViewingProfileId(member.user_id)}
                          >
                            {member.name}
                          </p>
                          {member.username && (
                            <span className="text-sm text-stone-500">@{member.username}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-stone-500 mt-1 flex-wrap">
                          {member.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5" />
                              {member.location}
                            </span>
                          )}
                        </div>
                        {member.bio && (
                          <p className="text-sm text-stone-600 mt-1 line-clamp-1">{member.bio}</p>
                        )}
                      </div>

                      {/* Actions - Blue Profile button with white text */}
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => setViewingProfileId(member.user_id)}
                          className="hidden sm:flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                          data-testid={`view-profile-${member.user_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1 text-white" />
                          <span className="text-sm font-bold text-white">Profile</span>
                        </button>
                        <button
                          onClick={() => startConversationWithMember(member)}
                          className="flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                          data-testid={`message-member-${member.user_id}`}
                        >
                          <MessageCircle className="w-4 h-4 sm:mr-1 text-white" />
                          <span className="hidden sm:inline text-sm font-bold text-white">Message</span>
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Load More */}
                  {hasMoreMembers && !memberSearch && (
                    <div className="pt-4 text-center">
                      <Button
                        variant="outline"
                        onClick={loadMoreMembers}
                        disabled={loadingMembers}
                        data-testid="load-more-members"
                      >
                        {loadingMembers ? (
                          <>
                            <div className="spinner w-4 h-4 border-green-600 border-t-transparent mr-2" />
                            Loading...
                          </>
                        ) : (
                          'Load More Golfers'
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Messages Tab - Real Private Messaging */}
      {activeTab === 'messages' && (
        <>
          {/* New Chat Modal */}
          {showNewChatModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      {isGroupChat ? 'Create Group Chat' : 'New Message'}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => {
                      setShowNewChatModal(false);
                      setSelectedUsers([]);
                      setGroupName('');
                      setIsGroupChat(false);
                    }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Toggle Group Chat */}
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Group Chat</Label>
                    <button
                      onClick={() => setIsGroupChat(!isGroupChat)}
                      className={`w-12 h-6 rounded-full transition-colors ${isGroupChat ? 'bg-green-600' : 'bg-stone-300'}`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${isGroupChat ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {/* Group Name */}
                  {isGroupChat && (
                    <div className="space-y-2">
                      <Label>Group Name</Label>
                      <Input
                        value={groupName}
                        onChange={(e) => setGroupName(e.target.value)}
                        placeholder="Enter group name..."
                      />
                    </div>
                  )}

                  {/* User Search */}
                  <div className="space-y-2">
                    <Label>{isGroupChat ? 'Add Members' : 'Search User'}</Label>
                    <Input
                      value={userSearch}
                      onChange={(e) => handleSearchUsers(e.target.value)}
                      placeholder="Search by name..."
                    />
                    {searchingUsers && <p className="text-xs text-stone-400">Searching...</p>}
                    {searchResults.length > 0 && (
                      <div className="max-h-32 overflow-y-auto border rounded-lg">
                        {searchResults.map(u => (
                          <div
                            key={u.user_id}
                            onClick={() => handleSelectUser(u)}
                            className="p-2 hover:bg-stone-50 cursor-pointer flex items-center gap-2"
                          >
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <span className="text-green-700 text-sm font-medium">{u.name?.charAt(0)}</span>
                            </div>
                            <span className="text-sm">{u.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Selected Users */}
                  {selectedUsers.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs">Selected ({selectedUsers.length})</Label>
                      <div className="flex flex-wrap gap-2">
                        {selectedUsers.map(u => (
                          <span
                            key={u.user_id}
                            className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm flex items-center gap-1"
                          >
                            {u.name}
                            <button onClick={() => handleSelectUser(u)} className="hover:text-red-500">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleCreateConversation}
                    disabled={selectedUsers.length === 0 || creatingChat || (isGroupChat && !groupName.trim())}
                    className="btn-primary w-full"
                  >
                    {creatingChat ? (
                      <div className="spinner w-4 h-4 border-white border-t-transparent" />
                    ) : (
                      isGroupChat ? 'Create Group' : 'Start Chat'
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          <Card className="overflow-hidden">
            <div className="flex h-[600px]">
              {/* Conversations List */}
              <div className={`w-full md:w-80 lg:w-96 border-r border-stone-200 flex-shrink-0 ${selectedConversation ? 'hidden md:block' : ''}`}>
                <div className="p-4 border-b border-stone-100 flex items-center justify-between">
                  <h3 className="font-semibold text-stone-900">Messages</h3>
                  <Button 
                    size="sm" 
                    onClick={() => setShowNewChatModal(true)}
                    className="btn-primary h-8 px-3"
                  >
                    <PlusCircle className="w-4 h-4 mr-1" />
                    New
                  </Button>
                </div>
                <div className="overflow-y-auto h-[calc(600px-65px)]">
                  {loadingConversations ? (
                    <div className="flex items-center justify-center h-32">
                      <div className="spinner" />
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="p-6 text-center text-stone-400">
                      <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm mb-3">No conversations yet</p>
                      <Button size="sm" onClick={() => setShowNewChatModal(true)} className="btn-primary">
                        Start a Chat
                      </Button>
                    </div>
                  ) : (
                    conversations.map(conv => (
                      <div
                        key={conv.conversation_id}
                        onClick={() => handleSelectConversation(conv)}
                        className={`p-4 border-b border-stone-100 cursor-pointer hover:bg-stone-50 transition-colors ${
                          selectedConversation?.conversation_id === conv.conversation_id ? 'bg-blue-50' : ''
                        } ${conv.unread_count > 0 ? 'bg-blue-50/50' : ''}`}
                        data-testid={`conversation-${conv.conversation_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-blue-600">
                              <span className="font-semibold text-white">
                                {getConversationAvatar(conv)}
                              </span>
                            </div>
                            {conv.unread_count > 0 && (
                              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className={`font-medium truncate ${conv.unread_count > 0 ? 'text-stone-900 font-bold' : 'text-stone-700'}`}>
                                {getConversationName(conv)}
                              </p>
                              <span className={`text-xs ${conv.unread_count > 0 ? 'text-blue-600 font-medium' : 'text-stone-400'}`}>
                                {formatMessageTime(conv.last_message_at)}
                              </span>
                            </div>
                            <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-stone-900 font-semibold' : 'text-stone-500'}`}>
                              {conv.last_message || 'No messages yet'}
                            </p>
                            {conv.is_group && (
                              <p className="text-xs text-blue-500">{conv.participants?.length} members</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Chat Area */}
              <div className={`flex-1 flex flex-col ${!selectedConversation && 'hidden md:flex'}`}>
                {selectedConversation ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-4 border-b border-stone-200 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <button 
                          onClick={() => setSelectedConversation(null)}
                          className="md:hidden p-1 hover:bg-stone-100 rounded"
                        >
                          <ChevronRight className="w-5 h-5 rotate-180" />
                        </button>
                        <button 
                          onClick={() => !selectedConversation.is_group && selectedConversation.other_user && setViewingProfileId(selectedConversation.other_user.user_id)}
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            selectedConversation.is_group ? 'bg-blue-600' : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                          }`}
                          title={!selectedConversation.is_group ? "View profile" : ""}
                        >
                          <span className="font-semibold text-white">
                            {getConversationAvatar(selectedConversation)}
                          </span>
                        </button>
                        <div>
                          <p className="font-semibold text-stone-900">{getConversationName(selectedConversation)}</p>
                          {selectedConversation.is_group ? (
                            <p className="text-xs text-purple-500">{selectedConversation.participants?.length} members</p>
                          ) : (
                            <button 
                              onClick={() => selectedConversation.other_user && setViewingProfileId(selectedConversation.other_user.user_id)}
                              className="text-xs text-green-600 hover:underline"
                            >
                              View Profile
                            </button>
                          )}
                        </div>
                      </div>
                      {!selectedConversation.is_group && selectedConversation.other_user && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewingProfileId(selectedConversation.other_user.user_id)}
                          title="View profile"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-stone-50">
                      {loadingMessages ? (
                        <div className="flex items-center justify-center h-full">
                          <div className="spinner" />
                        </div>
                      ) : chatMessages.length === 0 ? (
                        <div className="flex items-center justify-center h-full text-stone-400">
                          <div className="text-center">
                            <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p>No messages yet. Say hello!</p>
                          </div>
                        </div>
                      ) : (
                        chatMessages.map(msg => (
                          <div
                            key={msg.message_id}
                            className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[70%] ${
                              msg.sender_id === user?.user_id 
                                ? 'bg-blue-600 text-white rounded-2xl rounded-br-md' 
                                : 'bg-white text-stone-800 rounded-2xl rounded-bl-md shadow-sm'
                            } px-4 py-2`}>
                              {selectedConversation.is_group && msg.sender_id !== user?.user_id && (
                                <p className="text-xs font-medium text-green-300 mb-1">{msg.sender_name}</p>
                              )}
                              <p className="text-sm">{msg.content}</p>
                              <p className={`text-xs mt-1 ${msg.sender_id === user?.user_id ? 'text-green-200' : 'text-stone-400'}`}>
                                {formatMessageTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                      <div ref={chatEndRef} />
                    </div>

                    {/* Message Input */}
                    <div className="p-4 border-t border-stone-200 bg-white">
                      <div className="flex gap-2">
                        <Input
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                          placeholder="Type a message..."
                          className="flex-1"
                          data-testid="chat-message-input"
                        />
                        <Button 
                          onClick={handleSendMessage}
                          disabled={!message.trim() || sendingMessage}
                          className="btn-primary px-4"
                          data-testid="send-message-btn"
                        >
                          {sendingMessage ? (
                            <div className="spinner w-5 h-5 border-white border-t-transparent" />
                          ) : (
                            <Send className="w-5 h-5" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center bg-stone-50">
                    <div className="text-center text-stone-400">
                      <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="font-medium">Select a conversation</p>
                      <p className="text-sm mb-4">or start a new one</p>
                      <Button onClick={() => setShowNewChatModal(true)} className="btn-primary">
                        <PlusCircle className="w-4 h-4 mr-2" />
                        New Chat
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </>
      )}

      {/* Educational Fine Print */}
      <div className="mt-8 p-4 bg-stone-50 rounded-xl border border-stone-200">
        <p className="text-xs text-stone-500 leading-relaxed">
          <strong className="text-stone-600">About Golf Connect:</strong> Golf Connect helps you find playing partners in your area based on handicap, location, and preferences. All users must have a verified handicap (minimum 3 rounds) and an active subscription to participate. Messages are private between users. By using Golf Connect, you agree to respectful communication and meeting in public golf course settings. Par Seeker does not conduct background checks on users. Always exercise caution when meeting new playing partners. Report inappropriate behavior to support@parseeker.com.
        </p>
      </div>

      {/* Coming Soon Note */}
      <p className="text-center text-sm text-stone-400 mt-4">
        Golf Connect is being built. Be one of the first to connect!
      </p>
    </div>
  );
};

export default FindGolfersPage;
